import API from "../API";
import router from "../router";

export default {
    namespaced: true,

    state: {
        userId: null,
        userName: '',
        role: null,
        authenticationStatus: false,
        customerId: null, // Customer context from FlutterFlow handoff
        sessionRestorationInProgress: false, // Prevent race conditions during session restoration
    },

    getters: {
        hasUserAccess(state, getters) {
            // Online portal users (FlutterFlow handoff) have access to create and edit trims
            if (getters.isOnlinePortalUser) {
                return true;
            }
            return state.authenticationStatus && state.role === 'user' || getters.hasAdminAccess;
        },

        hasAdminAccess(state) {
            return state.authenticationStatus && state.role === 'admin';
        },

        isOnlinePortalUser() {
            // Online portal users are those who came through FlutterFlow handoff
            return localStorage.getItem('handoff-session') === 'true';
        },

        hasCustomerContext(state) {
            return state.customerId !== null && state.customerId !== undefined;
        },

        customerContext(state) {
            const result = state.customerId ? { customer_id: state.customerId } : null;
            console.log(`🔍 CustomerContext getter called - customerId: ${state.customerId}, returning:`, result);
            return result;
        },
    },

    mutations: {
        setUserId(state, id) {
            state.userId = id;
        },

        setUserName(state, name) {
            state.userName = name;
        },

        setRole(state, role) {
            state.role = role;
        },

        setAuthenticationStatus(state, status) {
            state.authenticationStatus = status;
        },

        setCustomerId(state, customerId) {
            console.log(`🔍 setCustomerId mutation called with: ${customerId}`);
            state.customerId = customerId;
            console.log(`🔍 State after mutation - customerId: ${state.customerId}`);
        },

        clearCustomerId(state) {
            console.log('🔍 clearCustomerId mutation called');
            console.log(`🔍 Previous customerId: ${state.customerId}`);
            state.customerId = null;
            console.log('🔍 State after clear - customerId is now null');
        },

        setSessionRestorationInProgress(state, inProgress) {
            state.sessionRestorationInProgress = inProgress;
        },

    },

    actions: {
        registration({dispatch}, {userName, email, password, role = 'user'}) {
            return new Promise((resolve, reject) => {

                API.authentication.registration({userName, email, password, role})
                    .then(async (resData) => {
                        let { id, userName, role } = resData;

                        await dispatch('userAuth', { id, userName, role, authStatus: true });

                        resolve();

                    }, (resError) => {
                        reject(resError.data);
                    });
            });
        },

        login({dispatch}, {email, password}) {
            return new Promise((resolve, reject) => {

                API.authentication.login({email, password})
                    .then(async (resData) => {

                        // when user is login from the trim page, first need to update the Opened Trim Status
                        await dispatch('trims/updateOpenedTrimStatus', true, {root: true});

                        let { id, userName, role } = resData;

                        await dispatch('userAuth', { id, userName, role, authStatus: true });

                        resolve();

                    }, (resError) => {
                        reject(resError.data);
                    });
            });
        },

        logout({state, dispatch}) {
            return new Promise(resolve => {
                let data = {
                    id: state.userId,
                };

                // when user is logout from the trim page, first need to update the Opened Trim Status
                dispatch('trims/updateOpenedTrimStatus', false, {root: true})
                    .then(() => {

                        API.authentication.logout(data)
                            .then(resData => {
                                    let { success } = resData;

                                    if (!success) return;

                                    // reset user data (includes customer context)
                                    dispatch('userAuthReset');

                                    // redirect user to home page
                                    router.push({ to: '/' });

                                    resolve();
                                },
                                (error) => {
                                    console.log(error);
                                });
                    });
            });
        },

        userAuth({commit, dispatch}, { id, userName, role, authStatus, customerId = null }) {
            commit('setUserId', id);
            commit('setUserName', userName);
            commit('setRole', role);
            commit('setAuthenticationStatus', authStatus);
            
            // Set customer context if provided
            if (customerId !== null) {
                commit('setCustomerId', customerId);
            }

            // socket connection only for authorized users
            if (authStatus) {
                dispatch('sockets/connect', {id, role}, {root: true});
            }
            else {
                dispatch('sockets/disconnect', null, {root: true});
            }
        },

        userAuthReset({commit, dispatch}) {
            // Clear customer context
            commit('clearCustomerId');
            // Reset authentication state
            dispatch('userAuth', { id: null, userName: '', role: null, authStatus: false })
        },

        authentication({dispatch}) {
            return new Promise((resolve, reject) => {
                API.authentication.auth()
                    .then(async (resData) => {
                        let { id, userName, role } = resData;

                        await dispatch('userAuth', { id, userName, role, authStatus: true });

                        resolve();
                    })
                    .catch((err) => {
                        reject(err)
                    });
            });
        },

        forgotPassword(ctx, {email}) {
            return new Promise((resolve, reject) => {
                API.authentication.forgotPassword({email})
                    .then(resData => {
                        const { error } = resData;

                        if (error) {
                            reject(error)
                        }
                        else {
                            resolve();
                        }
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        resetPassword({dispatch}, data) {
            return new Promise((resolve, reject) => {
                API.authentication.resetPassword(data)
                    .then(async (resData) => {
                        const { error } = resData;

                        if (error) {
                            reject(error)
                        }
                        else {
                            let { id, userName, role } = resData;

                            await dispatch('userAuth', { id, userName, role, authStatus: true });

                            resolve();
                        }
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },


        // Establish Supabase session from handoff token validation
        establishSupabaseSession({dispatch}, {user, session, customer_context, fromHandoff = false}) {
            return new Promise((resolve, reject) => {
                try {
                    const customerId = customer_context?.customer_id || null;
                    console.log(`🔍 Establishing Supabase session for user: ${user.email}${customerId ? ` with customer context: ${customerId}` : ''}`);
                    console.log('🔍 Session data received:', {
                        hasSession: !!session,
                        hasAccessToken: !!session?.access_token,
                        hasRefreshToken: !!session?.refresh_token,
                        hasExpiresAt: !!session?.expires_at,
                        accessTokenSample: session?.access_token ? session.access_token.substring(0, 10) + '...' : null,
                        expiresAt: session?.expires_at
                    });

                    // Store user authentication data in Vuex state (including customer context)
                    dispatch('userAuth', {
                        id: user.id,
                        userName: user.email, // Use email as userName for FlutterFlow users
                        role: 'user', // Default role for handoff users
                        authStatus: true,
                        customerId // Include customer context in session
                    });

                    // Store Supabase session data in localStorage for persistence
                    if (session && session.access_token) {
                        const sessionData = {
                            access_token: session.access_token,
                            refresh_token: session.refresh_token,
                            expires_at: session.expires_at,
                            user: {
                                id: user.id,
                                email: user.email,
                                metadata: user.metadata
                            },
                            customer_context, // Store customer context for session restoration
                            fromHandoff
                        };

                        localStorage.setItem('supabase-session', JSON.stringify(sessionData));
                        console.log('✅ Supabase session stored in localStorage with customer context');
                        console.log('🔍 Session data stored:', {
                            hasAccessToken: true,
                            sessionLength: JSON.stringify(sessionData).length,
                            customerId
                        });
                    } else {
                        console.error('❌ Session storage failed: Missing session or access_token', {
                            hasSession: !!session,
                            hasAccessToken: !!session?.access_token,
                            sessionKeys: session ? Object.keys(session) : []
                        });
                    }

                    // Update authentication status to indicate successful handoff
                    if (fromHandoff) {
                        // Mark this as a handoff session for special handling
                        localStorage.setItem('handoff-session', 'true');
                        console.log(`✅ Handoff session established successfully${customerId ? ` with customer ID: ${customerId}` : ''}`);
                    }

                    resolve({ user, customer_context });

                } catch (error) {
                    console.error('❌ Failed to establish Supabase session:', error);
                    reject(error);
                }
            });
        },

        // Clear handoff session data
        clearHandoffSession({dispatch}) {
            return new Promise((resolve) => {
                // Remove handoff-specific session data
                localStorage.removeItem('supabase-session');
                localStorage.removeItem('handoff-session');

                // Reset authentication state (includes customer context)
                dispatch('userAuthReset');

                console.log('Handoff session and customer context cleared');
                resolve();
            });
        },

        // Check if current session is from handoff
        isHandoffSession() {
            return localStorage.getItem('handoff-session') === 'true';
        },

        // Restore session from localStorage (for page refreshes)
        restoreHandoffSession({dispatch}) {
            return new Promise((resolve, reject) => {
                try {
                    const sessionData = localStorage.getItem('supabase-session');
                    const isHandoffSession = localStorage.getItem('handoff-session') === 'true';

                    console.log('🔍 restoreHandoffSession debug:', {
                        hasSessionData: !!sessionData,
                        sessionDataLength: sessionData ? sessionData.length : 0,
                        isHandoffSession,
                        handoffSessionValue: localStorage.getItem('handoff-session')
                    });

                    if (sessionData && isHandoffSession) {
                        const session = JSON.parse(sessionData);
                        
                        // Validate session data structure
                        if (!session.user || !session.expires_at) {
                            console.error('Invalid session data structure');
                            dispatch('clearHandoffSession');
                            reject(new Error('Invalid session data'));
                            return;
                        }
                        
                        // Check if session is still valid
                        const now = new Date();
                        const expiresAt = new Date(session.expires_at);
                        
                        if (expiresAt > now) {
                            // Session is still valid, restore authentication state with customer context
                            const customerId = session.customer_context?.customer_id || null;
                            
                            // Use Promise to ensure state update is complete
                            Promise.resolve(dispatch('userAuth', {
                                id: session.user.id,
                                userName: session.user.email,
                                role: 'user',
                                authStatus: true,
                                customerId // Restore customer context
                            })).then(() => {
                                const timeRemaining = Math.floor((expiresAt - now) / (1000 * 60)); // minutes
                                console.log(`✅ Handoff session restored from localStorage${customerId ? ` with customer ID: ${customerId}` : ''}`);
                                console.log(`🏢 Customer context restored: ${customerId ? `customer_id = ${customerId}` : 'none'}`);
                                console.log(`⏰ Session expires in ${timeRemaining} minutes`);
                                resolve({ session, customer_context: session.customer_context });
                            }).catch((error) => {
                                console.error('Failed to update authentication state:', error);
                                dispatch('clearHandoffSession');
                                reject(error);
                            });
                        } else {
                            // Session expired, clear it
                            const expiredMinutes = Math.floor((now - expiresAt) / (1000 * 60));
                            console.log(`⏰ Stored handoff session expired ${expiredMinutes} minutes ago`);
                            dispatch('clearHandoffSession');
                            reject(new Error('Session expired'));
                        }
                    } else {
                        // No handoff session to restore
                        resolve(null);
                    }
                } catch (error) {
                    console.error('Failed to restore handoff session:', error);
                    dispatch('clearHandoffSession');
                    reject(error);
                }
            });
        },

        // Validate current session for route protection
        async validateCurrentSession({state, dispatch, commit}) {
            try {
                console.log('🔍 Session validation started', { 
                    authenticationStatus: state.authenticationStatus,
                    sessionRestorationInProgress: state.sessionRestorationInProgress,
                    hasHandoffSession: localStorage.getItem('handoff-session') === 'true'
                });

                // If session restoration is already in progress, wait for it to complete
                if (state.sessionRestorationInProgress) {
                    console.log('⏳ Session restoration already in progress, waiting...');
                    let attempts = 0;
                    while (state.sessionRestorationInProgress && attempts < 100) { // Max 5 seconds
                        await new Promise(resolve => setTimeout(resolve, 50));
                        attempts++;
                    }
                    console.log(`⏳ Waited ${attempts * 50}ms for session restoration to complete`);
                }

                // If user is already authenticated, verify session is still valid
                if (state.authenticationStatus) {
                    const sessionData = localStorage.getItem('supabase-session');
                    const isHandoffSession = localStorage.getItem('handoff-session') === 'true';
                    
                    if (sessionData && isHandoffSession) {
                        try {
                            const session = JSON.parse(sessionData);
                            const now = new Date();
                            const expiresAt = new Date(session.expires_at);
                            
                            if (expiresAt > now) {
                                console.log('✅ Current session is valid');
                                return true;
                            } else {
                                console.log('⏰ Current session has expired, clearing...');
                                await dispatch('clearHandoffSession');
                                return false;
                            }
                        } catch (error) {
                            console.error('Error validating current session:', error);
                            await dispatch('clearHandoffSession');
                            return false;
                        }
                    } else {
                        console.log('✅ User authenticated (non-handoff session)');
                        return true;
                    }
                }

                // Not authenticated in state - try to restore handoff session
                const isHandoffSession = localStorage.getItem('handoff-session') === 'true';
                if (isHandoffSession) {
                    try {
                        // Prevent concurrent restoration attempts
                        commit('setSessionRestorationInProgress', true);
                        
                        console.log('🔄 Attempting to restore handoff session...');
                        const sessionResult = await dispatch('restoreHandoffSession');
                        
                        if (sessionResult) {
                            // Additional verification that authentication state is set
                            if (state.authenticationStatus) {
                                console.log('✅ Handoff session restored and state updated successfully');
                                return true;
                            } else {
                                console.error('❌ Session restored but authentication state not updated');
                                await dispatch('clearHandoffSession');
                                return false;
                            }
                        } else {
                            console.log('⚠️ No handoff session to restore');
                            return false;
                        }
                    } catch (error) {
                        console.log('❌ Handoff session restoration failed:', error.message);
                        // Session restoration failed (expired/invalid)
                        return false;
                    } finally {
                        commit('setSessionRestorationInProgress', false);
                    }
                }

                // No handoff session and not authenticated in state
                console.log('❌ No active authentication session');
                return false;

            } catch (error) {
                console.error('Session validation error:', error);
                return false;
            }
        },

    },

}